import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const CustomerRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    caregiver_name: '',
    caregiver_phone: '',
    caregiver_email: '',
    timezone: 'America/Los_Angeles'
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { token } = useAuth();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customers/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('Customer registered successfully!');
        setFormData({
          name: '',
          phone_number: '',
          caregiver_name: '',
          caregiver_phone: '',
          caregiver_email: '',
          timezone: 'America/Los_Angeles'
        });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to register customer');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0px',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <h1 style={{
        color: '#333',
        fontSize: '28px',
        marginBottom: '10px',
        textAlign: 'center'
      }}>
        Register New Customer
      </h1>
      
      <p style={{
        color: '#666',
        fontSize: '16px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        Add a new customer and their primary caregiver to the wellness check system
      </p>

      {success && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '5px',
          border: '1px solid #c3e6cb'
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '5px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#f8f9fa',
        padding: '30px',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        {/* Customer Information Section */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            color: '#333',
            fontSize: '20px',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #007bff'
          }}>
            Customer Information
          </h2>
          
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Customer First Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Customer Phone Number *
              </label>
              <input
                type="text"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                required
                placeholder="+1 (555) 123-4567"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
              <small style={{ color: '#666', fontSize: '14px' }}>
                Include country code (e.g., +1 for US)
              </small>
            </div>
          </div>
        </div>

        {/* Caregiver Information Section */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            color: '#333',
            fontSize: '20px',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #007bff'
          }}>
            Primary Caregiver Information
          </h2>
          
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Caregiver First Name *
              </label>
              <input
                type="text"
                value={formData.caregiver_name}
                onChange={(e) => handleInputChange('caregiver_name', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Caregiver Phone Number *
              </label>
              <input
                type="text"
                value={formData.caregiver_phone}
                onChange={(e) => handleInputChange('caregiver_phone', e.target.value)}
                required
                placeholder="+1 (555) 123-4567"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
              <small style={{ color: '#666', fontSize: '14px' }}>
                Include country code (e.g., +1 for US)
              </small>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Caregiver Email *
              </label>
              <input
                type="email"
                value={formData.caregiver_email}
                onChange={(e) => handleInputChange('caregiver_email', e.target.value)}
                required
                placeholder="caregiver@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ flex: '1', minWidth: '250px' }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                Timezone *
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff'
                }}
              >
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Australia/Sydney">Sydney (AEDT)</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: '#007bff',
              color: '#fff',
              padding: '15px 40px',
              fontSize: '18px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Registering...' : 'Register Customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerRegistration; 